import type { Address } from "viem"
import type { Config } from "wagmi"
import { action, computed, makeObservable, observable, override, runInAction } from "mobx"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { EventEmitter } from "@/domains/library"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { getAmountsForLiquidity, getSqrtRatioAtTick } from "@/domains/manual-cl-core"

import { THICK_V2_BASE_ADDRESSES } from "../constants"
import { burnThickNfpInteraction } from "../interactions"
import type { ManualClPositionRepository } from "../positions/manual-cl-position-repository"
import type { ManualClPositionStore } from "../positions/manual-cl-position-store"
import { humanizeTransactionError } from "../utils/humanize-transaction-error"
import { ManagePositionStoreBase } from "./manage-position-store-base"

/**
 * Manage-position store for the "burn (close)" flow.
 *
 * Burning a position is a single multicall that decreases remaining liquidity,
 * collects all accrued fees, and burns the NFT. This store:
 *
 *   - Computes `amount0Min` / `amount1Min` by projecting full-liquidity
 *     withdrawal through `getAmountsForLiquidity` and applying slippage.
 *   - Requires an explicit `confirmed` flag before `submit()` proceeds — the
 *     UI uses this for a destructive-action confirmation dialog.
 *   - Publishes `ManualClPositionBurnedEvent` on success so the position
 *     orchestrator marks the position as burned and re-runs its queries.
 */
export class BurnPositionStore extends ManagePositionStoreBase {
  confirmed: boolean = false
  isSubmitting: boolean = false
  submissionError: string | undefined = undefined

  constructor(
    positionRepository: ManualClPositionRepository,
    private readonly chainId: number,
    private readonly config: Config,
    private readonly eventBus: IEventBus,
    private readonly emitter: EventEmitter<EmitEvent>,
    private readonly walletStore: IWalletConnectionStore,
  ) {
    super(positionRepository)
    makeObservable(this, {
      confirmed: observable,
      isSubmitting: observable,
      submissionError: observable,

      setConfirmed: action,
      submit: action,
      reset: override,

      expectedAmount0: computed,
      expectedAmount1: computed,
      amount0Min: computed,
      amount1Min: computed,
      canSubmit: computed,
    })
  }

  private get activePosition(): ManualClPositionStore | undefined {
    return this.position
  }

  setConfirmed(confirmed: boolean): void {
    this.confirmed = confirmed
  }

  private get resolvedSqrtPriceX96(): bigint | undefined {
    const pool = this.activePosition?.pool
    if (!pool) return undefined
    if (pool.currentSqrtPriceX96 !== undefined) return pool.currentSqrtPriceX96
    if (pool.currentTick === undefined) return undefined
    try {
      return getSqrtRatioAtTick(pool.currentTick)
    } catch {
      return undefined
    }
  }

  get expectedAmount0(): bigint {
    const position = this.activePosition
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (!position || position.liquidity === 0n) return 0n
    const fallback = position.underlyingAmount0
    if (sqrtPriceX96 === undefined) return fallback
    try {
      const { amount0 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: position.tickLower,
        tickUpper: position.tickUpper,
        liquidity: position.liquidity,
      })
      if (amount0 === 0n && fallback > 0n) return fallback
      return amount0
    } catch {
      return fallback
    }
  }

  get expectedAmount1(): bigint {
    const position = this.activePosition
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (!position || position.liquidity === 0n) return 0n
    const fallback = position.underlyingAmount1
    if (sqrtPriceX96 === undefined) return fallback
    try {
      const { amount1 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: position.tickLower,
        tickUpper: position.tickUpper,
        liquidity: position.liquidity,
      })
      if (amount1 === 0n && fallback > 0n) return fallback
      return amount1
    } catch {
      return fallback
    }
  }

  get amount0Min(): bigint {
    if (this.expectedAmount0 === 0n) return 0n
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (this.expectedAmount0 * slippageMultiplier) / 10_000n
  }

  get amount1Min(): bigint {
    if (this.expectedAmount1 === 0n) return 0n
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (this.expectedAmount1 * slippageMultiplier) / 10_000n
  }

  get canSubmit(): boolean {
    if (!this.activePosition) return false
    if (this.isSubmitting) return false
    if (!this.walletStore.isConnected) return false
    if (this.activeNfpmTokenId === undefined) return false
    return this.confirmed
  }

  async submit(): Promise<void> {
    const position = this.activePosition
    if (!position) return
    if (!this.confirmed) return
    const nfpmTokenId = this.activeNfpmTokenId
    if (nfpmTokenId === undefined) {
      runInAction(() => {
        this.submissionError = "NFP ID is still resolving. Please try again in a moment."
      })
      return
    }
    const account = this.walletStore.connectedAddress?.raw
    if (!account) return

    runInAction(() => {
      this.isSubmitting = true
      this.submissionError = undefined
    })

    try {
      const deadline = this.getSubmitDeadline()
      const result = await burnThickNfpInteraction({
        account,
        chainId: this.chainId,
        config: this.config,
        tokenId: nfpmTokenId,
        poolAddress: position.pool.address.raw as Address,
        liquidity: position.liquidity,
        amount0Min: this.amount0Min,
        amount1Min: this.amount1Min,
        slippagePercent: this.slippage,
        deadline,
        nfpManagerAddress: THICK_V2_BASE_ADDRESSES.nfPositionManager,
        emitter: this.emitter,
        eventBus: this.eventBus,
      })

      if (result.success === false) {
        const humanized = humanizeTransactionError(result.error)
        if (humanized !== null) {
          runInAction(() => {
            this.submissionError = humanized
          })
        }
      }
    } catch (error) {
      const humanized = humanizeTransactionError(error)
      if (humanized !== null) {
        runInAction(() => {
          this.submissionError = humanized
        })
      }
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  reset(): void {
    super.reset()
    this.confirmed = false
    this.isSubmitting = false
    this.submissionError = undefined
  }
}
